import { getFundraiserProfile } from '../data/fundraiser-profile.config';
import type {
  FundraiserProfile,
  FundraiserScenario,
  FundraiserTransaction,
  MoneyCents,
} from '../models/fundraising-comparison.models';

export class ScenarioFactory {
  createScenario(
    intendedGiftTotalCents: MoneyCents,
    profileId?: string,
    donationCountOverride: number | null = null,
  ): FundraiserScenario {
    const profile = getFundraiserProfile(profileId);
    const normalizedTotalCents = Math.max(
      0,
      Math.min(1_000_000_000, Math.round(intendedGiftTotalCents)),
    );
    const estimatedDonationCount =
      normalizedTotalCents === 0
        ? 0
        : Math.max(
            1,
            Math.round(
              (normalizedTotalCents * profile.donationCount) /
                profile.totalRaisedCents,
            ),
          );
    const requestedDonationCount =
      donationCountOverride === null
        ? estimatedDonationCount
        : Math.round(donationCountOverride);
    const donationCount =
      normalizedTotalCents === 0
        ? 0
        : Math.max(
            1,
            Math.min(
              100_000,
              normalizedTotalCents,
              requestedDonationCount,
            ),
          );

    return {
      id: `${profile.id}-${normalizedTotalCents}-${donationCount}`,
      label: profile.label,
      dataStatus: 'empirical-profile',
      profileId: profile.id,
      profileSourceStatus: profile.sourceStatus,
      donationCountSource:
        donationCountOverride === null ? 'profile' : 'override',
      intendedGiftTotalCents: normalizedTotalCents,
      transactions: this.scaleProfileTransactions(
        normalizedTotalCents,
        donationCount,
        profile,
      ),
    };
  }

  private scaleProfileTransactions(
    intendedGiftTotalCents: MoneyCents,
    donationCount: number,
    profile: FundraiserProfile,
  ): FundraiserTransaction[] {
    if (intendedGiftTotalCents === 0 || donationCount === 0) {
      return [];
    }

    const observedAmounts = profile.donationHistogram.flatMap((bin) =>
      Array.from({ length: bin.count }, () => bin.amountCents),
    );
    const weights = Array.from({ length: donationCount }, (_, index) => {
      const quantile = (index + 0.5) / donationCount;
      const observedIndex = Math.min(
        observedAmounts.length - 1,
        Math.floor(quantile * observedAmounts.length),
      );
      return observedAmounts[observedIndex];
    });
    const totalWeight = weights.reduce((total, weight) => total + weight, 0);
    // Reserve one cent per transaction so scaling never creates zero-dollar gifts.
    const distributableCents = intendedGiftTotalCents - donationCount;
    const allocations = weights.map((weight, index) => {
      const exactNumerator = distributableCents * weight;
      return {
        index,
        cents: 1 + Math.floor(exactNumerator / totalWeight),
        remainder: exactNumerator % totalWeight,
      };
    });

    const remainingCents =
      intendedGiftTotalCents -
      allocations.reduce((total, allocation) => total + allocation.cents, 0);

    const largestRemainders = [...allocations].sort(
      (left, right) =>
        right.remainder - left.remainder || left.index - right.index,
    );
    for (let index = 0; index < remainingCents; index += 1) {
      largestRemainders[index].cents += 1;
    }

    return allocations.map((allocation) => ({
      id: `${profile.id}-${allocation.index + 1}`,
      intendedGiftCents: allocation.cents,
    }));
  }
}
